<template>
    <div v-if="permissions.can('start_offboarding_export')" class="overflow-clip rounded-md border border-gray-200 bg-white">

        <!-- Row header — always visible -->
        <button
            type="button"
            class="flex w-full items-center gap-4 px-6 py-4 text-left transition-colors duration-150 hover:bg-gray-50"
            @click="expanded = !expanded"
        >
            <!-- Icon -->
            <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-500 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/>
                </svg>
            </div>

            <!-- Title + description -->
            <div class="flex-1">
                <p class="m-0 text-sm font-semibold text-gray-900">Offboarding Data Export</p>
                <p class="m-0 text-sm text-gray-500">Export a company's full point history and alarms as a zip to hand back to them.</p>
            </div>

            <!-- Chevron -->
            <svg class="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200"
                 :class="{ 'rotate-90': expanded }"
                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
        </button>

        <!-- Expanded filter panel -->
        <div v-if="expanded" class="border-t border-gray-200 bg-gray-50 px-6 py-5">

            <!-- Banners -->
            <div class="mb-4 flex flex-col gap-2">

                <div v-if="started.length" class="flex items-start gap-3 rounded-md border border-green-500/20 bg-green-50 px-4 py-3 border-l-4 border-l-green-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-green-800">Export Started</p>
                        <p class="m-0 text-sm text-green-700">
                            Started for {{ started.length }} {{ started.length === 1 ? 'company' : 'companies' }}. You will
                            receive an email with a download link for each one when it finishes — this can take a while for
                            sites with a lot of history.
                        </p>
                    </div>
                    <button type="button" class="text-green-500/70 hover:text-green-700/70" @click="started = []">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

                <div v-if="skipped.length" class="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-50 px-4 py-3 border-l-4 border-l-amber-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-amber-800">Some Companies Were Skipped</p>
                        <p v-for="entry in skipped" :key="entry.company_id" class="m-0 text-sm text-amber-700">
                            {{ entry.company_id }} — {{ entry.reason }}
                        </p>
                    </div>
                    <button type="button" class="text-amber-500/70 hover:text-amber-700/70" @click="skipped = []">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

                <div v-if="showError" class="flex items-start gap-3 rounded-md border border-red-500/20 bg-red-50 px-4 py-3 border-l-4 border-l-red-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-red-800">Export Failed To Start</p>
                        <p class="m-0 text-sm text-red-700">{{ errorMessage }}</p>
                    </div>
                    <button type="button" class="text-red-500/70 hover:text-red-700/70" @click="showError = false">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

            </div>

            <div class="grid grid-cols-1 gap-4">

                <!-- Company multi-select -->
                <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700" for="offboarding-export-companies">
                        Companies
                    </label>
                    <select2
                        id="offboarding-export-companies"
                        v-model:value="companyIds"
                        :options="companies"
                        name="company_ids"
                        :multiple="true"
                        :sort-by-text="true"
                    ></select2>
                    <p class="mt-2 text-sm text-gray-500">Each company is exported independently and produces its own zip and email.</p>
                </div>

                <!-- Date cap -->
                <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700" for="offboarding-export-start">
                        Don't export data before <span class="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                        id="offboarding-export-start"
                        v-model="dontExportBefore"
                        type="date"
                        class="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                    <p class="mt-2 text-sm text-gray-500">
                        Interpreted as start of day in each company's timezone, and applied to alarms as well as point data.
                    </p>
                </div>

                <!-- Scope toggles -->
                <div class="flex flex-col gap-2">
                    <label class="flex items-center gap-2 text-sm text-gray-700">
                        <input v-model="includeDiagnostic" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                        Include diagnostic points
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-700">
                        <input v-model="includeDefaultArea" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                        Include points on the default area
                    </label>
                </div>

            </div>

            <p class="mt-4 text-sm text-gray-500">
                Only generic and virtual points are exported. Sound, thermal and CCI points are counted in the manifest but
                their readings are not included.
            </p>

            <!-- Footer row -->
            <div class="mt-4 flex items-center justify-end">

                <button
                    type="button"
                    :disabled="exporting"
                    @click="handleExport"
                    class="inline-flex min-w-80 items-center justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-600 focus:outline-none active:bg-blue-700 disabled:opacity-50"
                >
                    <svg v-if="exporting" class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg"
                         fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    {{ exporting ? 'Starting...' : 'Start Offboarding Export' }}
                </button>
            </div>
        </div>

    </div>
</template>

<script>
import axios from "axios";
import Select2 from "@/components/elements/select2.vue";
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

export default {
    name: "OffboardingExportPanel",
    mixins: [SonicuCoreMixin],
    components: {Select2},
    props: {
        companies: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            expanded: false,
            companyIds: [],
            dontExportBefore: null,
            includeDiagnostic: false,
            includeDefaultArea: false,
            exporting: false,
            started: [],
            skipped: [],
            showError: false,
            errorMessage: 'Something went wrong. Please try again.',
        }
    },
    methods: {
        handleExport() {
            if (this.exporting) {
                return;
            }

            if (!this.companyIds.length) {
                this.errorMessage = 'Please select at least one company before exporting.';
                this.showError = true;
                return;
            }

            this.exporting = true;
            this.started = [];
            this.skipped = [];
            this.showError = false;

            axios.post('/api-internal/offboarding-exports', {
                company_ids: this.companyIds,
                dont_export_before: this.dontExportBefore || null,
                include_diagnostic: this.includeDiagnostic,
                include_default_area: this.includeDefaultArea,
            }).then(response => {
                this.started = response.data.started || [];
                this.skipped = response.data.skipped || [];
            }).catch(() => {
                this.errorMessage = 'Something went wrong. Please try again.';
                this.showError = true;
            }).finally(() => {
                this.exporting = false;
            });
        },
    },
}
</script>
